import { ReactNode, useState, useMemo } from 'react';
import { useAuth } from '../../contexts/Auth/AuthProvider';
import Drawer from './Drawer';
import { Link, useNavigate } from 'react-router-dom';
import { PATHS } from '../../routes/routes';
import userIcon from '../../assets/square-user.svg';
import logOutIcon from '../../assets/log-out.svg';
import { FaArrowLeftLong } from 'react-icons/fa6';
import { FaUser } from 'react-icons/fa6';
import SecureStorage from '../../utils/secureStorage';

type componentProp = {
    children: ReactNode;
    title: string;
    backIcon?: boolean;
    menuIcon?: boolean;
    fullHeight?: boolean;
};

// Helper function to decode email from customer token
function decodeEmailFromToken(bearerToken: string | null): string | null {
    if (!bearerToken) return null;
    const token = bearerToken.startsWith('Bearer ') ? bearerToken.slice(7) : bearerToken;
    const parts = token.split('.');
    if (parts.length < 2) return null;
    try {
        const payload = JSON.parse(atob(parts[1]));
        const emailRaw = payload?.email ?? payload?.sub ?? null;
        return emailRaw ? String(emailRaw).trim().toLowerCase() : null;
    } catch {
        return null;
    }
}

const Header: React.FC<componentProp> = ({ children, title, backIcon, menuIcon, fullHeight = true }) => {
    const { currentUser, handleLogout } = useAuth();
    const navigate = useNavigate();
    const [imgLoading, setImgLoading] = useState(currentUser?.imageUrl ? true : false);
    
    // Check for customer token
    const customerEmail = useMemo(() => decodeEmailFromToken(SecureStorage.getCustomerToken()), []);
    const isCustomer = !!customerEmail;
    const isClubOwner = !!currentUser;
    
    // Determine what type of page this is based on props and context
    // const isCustomerPage = menuIcon && isCustomer; // Customer portal with menu
    const isClubOwnerPage = menuIcon && isClubOwner; // Club owner pages with menu
    const isPublicPage = !menuIcon; // Public pages (signin, verification, etc.)
    
    // Show header logic:
    // - Club owner pages: only if club owner is logged in
    // - Customer pages: hide header completely for customers
    // - Public pages: always show (for signin, verification, etc.)
    const shouldShowHeader = isPublicPage || isClubOwnerPage;

    const handleImageLoad = () => {
        setImgLoading(false);
    };
    
    const handleCustomerLogout = () => {
        SecureStorage.removeCustomerToken();
        navigate(PATHS.CUSTOMER_SIGNIN);
    };

    return (
        <div>
            {shouldShowHeader && (
                <>
                    <header className="header">
                        <div className="flex items-center gap-6">
                            {menuIcon && (
                                <>
                                    {/* Only show Drawer for club owners, not customers */}
                                    {isClubOwner && <Drawer />}
                                    <div>
                                        <h1 className="text-xl text-left font-semibold bg-gradient-to-r from-violet-600 to-indigo-600 bg-clip-text text-transparent">
                                            {title}
                                        </h1>
                                        <p className="text-sm text-text-secondary">
                                            {isClubOwner ? currentUser?.email : customerEmail}
                                        </p>
                                    </div>
                                </>
                            )}
                            {backIcon && (
                                <div className="flex items-center gap-6">
                                    <div
                                        className="nav-button"
                                        onClick={() => {
                                            // If there is history, go back; otherwise, fallback to welcome
                                            if (window.history.state && window.history.state.idx > 0) {
                                                navigate(-1);
                                            } else {
                                                navigate(PATHS.WELCOME);
                                            }
                                        }}
                                    >
                                        <FaArrowLeftLong className="text-[18px]" />
                                    </div>
                                    <h1 className="text-xl text-left font-semibold bg-gradient-to-r from-violet-600 to-indigo-600 bg-clip-text text-transparent">
                                        {title}
                                    </h1>
                                </div>
                            )}
                        </div>
                        {menuIcon && (
                            <div className="dropdown dropdown-end inset-0">
                                <div tabIndex={0} role="button" className="avatar placeholder">
                                    <div className="w-10 rounded-full bg-gradient-to-r from-violet-600 to-indigo-600">
                                        {isClubOwner && currentUser?.imageUrl ? (
                                            <>
                                                {imgLoading && (
                                                    <span className="loading loading-spinner loading-sm"></span>
                                                )}
                                                <img
                                                    src={currentUser.imageUrl}
                                                    onLoad={handleImageLoad}
                                                    alt="user image"
                                                    className={imgLoading ? 'hidden' : ''}
                                                />
                                            </>
                                        ) : (
                                            <FaUser className="text-white" />
                                        )}
                                    </div>
                                </div>
                                <ul
                                    tabIndex={0}
                                    className="menu menu-sm dropdown-content bg-white rounded-lg z-[1000] mt-3 w-60 py-2 px-1 shadow-lg border-[1px] border-border"
                                    style={{ insetInlineEnd: '' }}>
                                    {isClubOwner && (
                                        <li className="mb-1">
                                            <Link to={PATHS.PROFILE}>
                                                <img src={userIcon} alt="add" width={18} />
                                                {currentUser?.email || 'Profile'}
                                            </Link>
                                        </li>
                                    )}
                                    <li>
                                        <Link 
                                            to={isCustomer ? PATHS.CUSTOMER_SIGNIN : PATHS.SIGNIN} 
                                            onClick={isCustomer ? handleCustomerLogout : handleLogout}
                                        >
                                            <img src={logOutIcon} alt="add" width={18} />
                                            Logout
                                        </Link>
                                    </li>
                                </ul>
                            </div>
                        )}
                    </header>

                    <div className={`main ${fullHeight ? 'min-h-screen' : ''} bg-page-body`}>{children}</div>
                </>
            )}
        </div>
    );
};

export default Header;
